import { Trans, useLingui } from "@lingui/react/macro";
import React, { useState } from "react";
import {
  SectionContainer,
  SectionTitleBody,
  SectionTitleHeading,
  SectionTitle
} from "@silversea-ssc/ui-components-react";
import Slider, { SliderSlide } from "components/Slider";
import OfferCard from "components/OfferCard/OfferCard";
import { dequal } from "dequal/lite";
import { FareCode } from "@silversea-ssc/web-fares";
import { showFareSpecificOfferDisclaimer } from "./helpers";
import { fareCodeLabel } from "features/fare/fares";
import { dateFromOptionalDate } from "src/utils/date";
import { ActivePromoForCruise } from "src/utils/promos";
import { ECommerceStatus } from "src/ssc-api/useECommerceStatus";
import { formatConjunction } from "src/utils/localization/formatConjunction";
import { useLanguage } from "src/utils/localization/languages/I18nContext";
const OfferPageModal = React.lazy(
  () => import("components/OfferPageModal/OfferPageModal")
);

type Props = {
  cruiseFares: FareCode[];
  activePromos: ActivePromoForCruise[];
  ecommerceStatus: ECommerceStatus;
};

type OfferPageOpenModalArgs = {
  promo: ActivePromoForCruise;
};

const CruiseSectionOffers: React.FC<React.PropsWithChildren<Props>> = ({
  cruiseFares,
  activePromos,
  ecommerceStatus
}) => {
  const language = useLanguage();
  const { t } = useLingui();
  const [openedModal, setOpenModal] = useState<
    OfferPageOpenModalArgs | undefined
  >(undefined);

  if (!activePromos || activePromos.length == 0)
    return (
      <SectionContainer
        spacingTop="regular"
        spacingBottom="regular"
        backgroundColor="neutral05"
        dataContentName=""
      ></SectionContainer>
    );

  return (
    <React.Fragment>
      <SectionContainer
        spacingTop="regular"
        spacingBottom="regular"
        backgroundColor="white"
        dataContentName="section_offers"
      >
        <SectionTitle variant="wide">
          <SectionTitleHeading>
            <Trans>Offers for this Cruise</Trans>
          </SectionTitleHeading>
          <SectionTitleBody>
            <Trans>All offers are subject to availability</Trans>
          </SectionTitleBody>
        </SectionTitle>
        <Slider variantColor="white">
          {activePromos.map((promo) => {
            const offerFareCodes = showFareSpecificOfferDisclaimer(
              cruiseFares,
              promo.activeForFares
            );
            const fareCodeLabels = offerFareCodes.map((f) =>
              t(fareCodeLabel(f))
            );
            const allFareCodeLabels = formatConjunction(
              fareCodeLabels,
              language.code
            );

            const disclaimer = allFareCodeLabels ? (
              <Trans id="cruise-promo-available-only-for-fares">
                Available only for {allFareCodeLabels}
              </Trans>
            ) : undefined;

            const content = promo.content.cardWithIcon;
            if (!content) return null;

            return (
              <SliderSlide key={promo._key}>
                <OfferCard
                  id={promo.code}
                  title={content.title?.localized}
                  description={content.description?.localized}
                  icon={content.icon}
                  iconUrl={promo.content.iconVertical?.asset?.url}
                  background="neutral05"
                  tokenValues={promo.tokenValues}
                  expirationDate={dateFromOptionalDate(promo.toDate)}
                  onClick={() =>
                    setOpenModal({
                      promo
                    })
                  }
                  trackName={promo.content?.metadata?.tracking?.name}
                  disclaimer={disclaimer}
                />
              </SliderSlide>
            );
          })}
        </Slider>
      </SectionContainer>

      {openedModal && (
        <React.Suspense fallback={<div></div>}>
          <OfferPageModal
            modal={{
              isOpen: !!openedModal,
              onRequestClose: () => setOpenModal(undefined)
            }}
            {...openedModal}
            ecommerceStatus={ecommerceStatus}
          />
        </React.Suspense>
      )}
    </React.Fragment>
  );
};

export default React.memo(CruiseSectionOffers, dequal);
